import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./ToolTechStack.css";

const ToolTechStack = () => {
  // AI Technologies Categories
  const aiCategories = [
    {
      title: "Machine Learning Frameworks",
      items: [
        {
          name: "TensorFlow",
          logo: "/logos/tech_tensor_flow.png",
          description: "End-to-end open source platform for machine learning",
        },
        {
          name: "PyTorch",
          logo: "/logos/tech_pytorch.png",
          description: "Open source machine learning framework",
        },
        {
          name: "Keras",
          logo: "/logos/tech_keras.png",
          description: "Deep learning API written in Python",
        },
        {
          name: "Scikit-learn",
          logo: "/logos/scikit_learn.png",
          description: "Machine learning library for Python",
        },
        {
          name: "MXNet",
          logo: "/logos/tech_mx.png",
          description:
            "Deep learning framework designed for efficiency and flexibility",
        },
        {
          name: "XGBoost",
          logo: "/logos/tech_xgb.png",
          description: "Optimized distributed gradient boosting library",
        },
        {
          name: "LightGBM",
          logo: "/logos/tech_light.png",
          description: "Gradient boosting framework by Microsoft",
        },
      ],
    },
    {
      title: "Deep Learning & Neural Networks",
      items: [
        {
          name: "OpenAI",
          logo: "/logos/tech_openai.png",
          description: "AI research and deployment company",
        },
        {
          name: "Hugging Face",
          logo: "/logos/tech_huggingface.png",
          description: "Library of pre-trained models for NLP",
        },
        {
          name: "Caffe",
          logo: "/logos/tech_caffe.png",
          description:
            "Deep learning framework made with expression, speed, and modularity in mind",
        },
        {
          name: "Theano",
          logo: "/logos/tech_theano.png",
          description:
            "Python library for defining, optimizing, and evaluating mathematical expressions",
        },
        {
          name: "CNTK",
          logo: "/logos/tech_cntk.png",
          description: "Microsoft Cognitive Toolkit for deep learning",
        },
        {
          name: "Fast.ai",
          logo: "/logos/tech_fastai.png",
          description:
            "Deep learning library that provides practitioners with high-level components",
        },
      ],
    },
    {
      title: "AI Development Tools",
      items: [
        {
          name: "Jupyter",
          logo: "/logos/tech_jupyter.png",
          description: "Web-based interactive computing platform",
        },
        {
          name: "Google Colab",
          logo: "/logos/tech_colab.png",
          description: "Cloud-based Jupyter notebook environment",
        },
        {
          name: "Anaconda",
          logo: "/logos/tech_anaconda.png",
          description: "Distribution of Python and R for scientific computing",
        },
        {
          name: "VS Code",
          logo: "/logos/tech_vscode.png",
          description:
            "Code editor redefined and optimized for building and debugging modern web and cloud applications",
        },
        {
          name: "MLflow",
          logo: "/logos/tech_mlflow.png",
          description: "Platform to manage the ML lifecycle",
        },
        {
          name: "Kubeflow",
          logo: "/logos/tech_kubeflow.png",
          description: "Machine learning toolkit for Kubernetes",
        },
        {
          name: "Weights & Biases",
          logo: "/logos/tech_weights.png",
          description:
            "Experiment tracking, dataset versioning, and model management",
        },
      ],
    },
    {
      title: "Computer Vision",
      items: [
        {
          name: "OpenCV",
          logo: "/logos/tech_opencs.png",
          description:
            "Open source computer vision and machine learning software library",
        },
        {
          name: "YOLO",
          logo: "/logos/tech_yolo.png",
          description: "Real-time object detection system",
        },
        {
          name: "Detectron2",
          logo: "/logos/tech_detect.png",
          description:
            "Facebook AI Research's next-generation library for object detection",
        },
        {
          name: "MediaPipe",
          logo: "/logos/tech_mediapipe.png",
          description: "Framework for building multimodal applied ML pipelines",
        },
        {
          name: "Dlib",
          logo: "/logos/tech_dlib.png",
          description:
            "Modern C++ toolkit containing machine learning algorithms",
        },
        {
          name: "SimpleCV",
          logo: "/logos/tech_simplecv.png",
          description:
            "Open source framework for building computer vision applications",
        },
      ],
    },
    {
      title: "Natural Language Processing",
      items: [
        {
          name: "NLTK",
          logo: "/logos/tech_nltk.png",
          description:
            "Platform for building Python programs to work with human language data",
        },
        {
          name: "spaCy",
          logo: "/logos/tech_spacy.png",
          description: "Advanced natural language processing in Python",
        },
        {
          name: "Gensim",
          logo: "/logos/tech_gensim.png",
          description: "Topic modeling for humans",
        },
        {
          name: "BERT",
          logo: "/logos/tech_bert.png",
          description:
            "Pre-training of deep bidirectional transformers for language understanding",
        },
        {
          name: "GPT",
          logo: "/logos/tech_gpt.png",
          description: "Generative pre-trained transformer models",
        },
        {
          name: "Transformer",
          logo: "/logos/tech_transformer.png",
          description:
            "Neural network architecture for sequence-to-sequence tasks",
        },
        {
          name: "Stanford NLP",
          logo: "/logos/tech_stanford.png",
          description: "Natural language processing toolkit",
        },
      ],
    },
    {
      title: "AI Cloud Platforms",
      items: [
        {
          name: "AWS SageMaker",
          logo: "/logos/tech_sagemeker.jpg",
          description:
            "Fully managed service to build, train, and deploy ML models",
        },
        {
          name: "Google AI Platform",
          logo: "/logos/tech_google.png",
          description: "End-to-end ML development on Google Cloud",
        },
        {
          name: "Azure Machine Learning",
          logo: "/logos/tech_azure_ml.png",
          description:
            "Cloud-based environment for training, deploying, and managing ML models",
        },
        {
          name: "IBM Watson",
          logo: "/logos/tech_ibm.png",
          description: "AI platform for business",
        },
        {
          name: "NVIDIA NGC",
          logo: "/logos/tech_nvidia.png",
          description: "GPU-optimized software for AI and data science",
        },
        {
          name: "Databricks",
          logo: "/logos/tech_databricks.png",
          description:
            "Unified data analytics platform for massive-scale data engineering and collaborative data science",
        },
      ],
    },
    {
      title: "Data Processing & Analytics",
      items: [
        {
          name: "Apache Spark",
          logo: "/logos/tech_apache.png",
          description: "Unified engine for large-scale data processing",
        },
        {
          name: "Hadoop",
          logo: "/logos/tech_hadoop.png",
          description:
            "Framework for distributed storage and processing of big data",
        },
        {
          name: "Apache Kafka",
          logo: "/logos/tech_kafka.png",
          description: "Distributed event streaming platform",
        },
        {
          name: "Tableau",
          logo: "/logos/tech_tableau.png",
          description: "Interactive data visualization software",
        },
        {
          name: "Power BI",
          logo: "/logos/tech_power_bi.png",
          description: "Business analytics service by Microsoft",
        },
        {
          name: "Apache Flink",
          logo: "/logos/tech_apacheflink.png",
          description: "Framework for stateful computations over data streams",
        },
      ],
    },
    {
      title: "AI Hardware & Acceleration",
      items: [
        {
          name: "NVIDIA CUDA",
          logo: "/logos/tech_nvidiacuda.png",
          description: "Parallel computing platform and API model",
        },
        {
          name: "TensorRT",
          logo: "/logos/tech_tensorrt.png",
          description: "SDK for high-performance deep learning inference",
        },
        {
          name: "OpenVINO",
          logo: "/logos/tech_openveno.png",
          description: "Toolkit for optimizing and deploying AI inference",
        },
        {
          name: "ROCm",
          logo: "/logos/tech_rocm.png",
          description: "Open software platform for GPU computing from AMD",
        },
        {
          name: "Google TPU",
          logo: "/logos/tech_googletpu.png",
          description:
            "Custom-developed processor for machine learning workloads",
        },
      ],
    },
  ];

  // General Tech Stack
  const techItemsRow1 = [
    { name: "React.js", logo: "/logos/tech_react.png" },
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "Vue.js", logo: "/logos/tech_vuejs.png" },
    { name: "Node.js", logo: "/logos/node js.png" },
    { name: "Python", logo: "/logos/tech_python.png" },
    { name: "TensorFlow", logo: "/logos/tech_tensor_flow.png" },
    { name: "PyTorch", logo: "/logos/tech_pytorch.png" },
    { name: "Docker", logo: "/logos/tech_docker.png" },
    { name: "Kubernetes", logo: "/logos/tech_kubernative.png" },
    { name: "AWS", logo: "/logos/tech_aws.png" },
    { name: "TypeScript", logo: "/logos/tech_typescript.png" },
    { name: "Java", logo: "/logos/tech_java.png" },
    { name: "Swift", logo: "/logos/tech_swift.png" },
    { name: "Kotlin", logo: "/logos/tech_kotlin.png" },
  ];

  const techItemsRow2 = [
    { name: "Azure", logo: "/logos/tech_azure.png" },
    { name: "GCP", logo: "/logos/tech_gcp.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "Redis", logo: "/logos/tech_redis.png" },
    { name: "Elasticsearch", logo: "/logos/tech_elasticsearch.png" },
    { name: "GraphQL", logo: "/logos/tech_graphql.png" },
    { name: "Git", logo: "/logos/tech_git.png" },
    { name: "Jenkins", logo: "/logos/tech_jenkins.png" },
    { name: "Terraform", logo: "/logos/tech_taraform.png" },
    { name: "Ansible", logo: "/logos/tech_ansible.png" },
    { name: "Prometheus", logo: "/logos/tech_prometheus.png" },
    { name: "Grafana", logo: "/logos/tech_grafana.png" },
  ];

  // Duplicate items for seamless animation
  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const [activeCategory, setActiveCategory] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1;

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  // Auto-rotate categories
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCategory((prev) => (prev + 1) % aiCategories.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [aiCategories.length]);

  return (
    <div className="tech-stack-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>
          AI Tools & Tech Stack | MK eTech - Advanced AI Development
        </title>
        <meta
          name="description"
          content="Explore MK eTech's comprehensive AI tools and tech stack including Machine Learning, Deep Learning, Computer Vision, NLP, and cloud AI platforms for cutting-edge solutions."
        />
        <meta
          name="keywords"
          content="AI Technology, Machine Learning, Deep Learning, Neural Networks, Computer Vision, Natural Language Processing, TensorFlow, PyTorch, OpenAI, AWS SageMaker, Azure ML, React, Node.js, Python, AI Development"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="tech-hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1>
              <span className="hero-title">AI Tools & Technology Stack</span>
              {/* <span className="hero-subtitle">Technology Stack</span> */}
            </h1>
            <p>
              At MK eTech, we leverage cutting-edge AI technologies and
              frameworks to build intelligent, scalable, and innovative
              solutions that drive business transformation.
            </p>
            <div className="hero-cta">
              <a href="/contact-us">
                <button className="cta-button primary">
                  Explore Our Solutions
                </button>
              </a>
              <a href="/contact-us">
                <button className="cta-button secondary">
                  Contact Our Experts
                </button>
              </a>
            </div>
          </div>
          <div className="hero-visual">
            <div className="ai-orb"></div>
            <div className="floating-tech">
              <div className="tech-icon" style={{ animationDelay: "0s" }}>
                <img src="/logos/tech_ai.png" alt="AI" />
              </div>
              <div className="tech-icon" style={{ animationDelay: "1s" }}>
                <img src="/logos/tech ml.png" alt="Machine Learning" />
              </div>
              <div className="tech-icon" style={{ animationDelay: "2s" }}>
                <img src="/logos/tech_cloud.png" alt="Cloud" />
              </div>
              <div className="tech-icon" style={{ animationDelay: "3s" }}>
                <img src="/logos/tech_data.png" alt="Data" />
              </div>
              <div className="tech-icon" style={{ animationDelay: "3s" }}>
                <img src="/logos/tech_kubernative.png" alt="Data" />
              </div>
            </div>
          </div>
        </div>
        <div className="hero-background">
          <div className="circuit-pattern"></div>
        </div>
      </section>

      {/* AI Technologies Section */}
      <section className="ai-technologies">
        <div className="container">
          <h2 className="section-title">
            <span className="title-text">Our AI Technology Expertise</span>
            <span className="title-underline"></span>
          </h2>
          <p className="section-description">
            We specialize in implementing advanced AI solutions across various
            domains
          </p>

          <div className="category-tabs">
            {aiCategories.map((category, index) => (
              <button
                key={index}
                className={`category-tab ${
                  activeCategory === index ? "active" : ""
                }`}
                onClick={() => setActiveCategory(index)}
              >
                {category.title}
              </button>
            ))}
          </div>

          <div className="ai-categories">
            {aiCategories.map((category, index) => (
              <div
                key={index}
                className={`ai-category ${
                  activeCategory === index ? "active" : ""
                }`}
              >
                <h3>{category.title}</h3>
                <div className="ai-tech-grid">
                  {category.items.map((tech, techIndex) => (
                    <div key={techIndex} className="ai-tech-item">
                      <div className="ai-tech-logo">
                        <img
                          src={tech.logo}
                          alt={tech.name}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/logos/default.png";
                          }}
                        />
                      </div>
                      <div className="ai-tech-info">
                        <span className="ai-tech-name">{tech.name}</span>
                        <p className="ai-tech-description">
                          {tech.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Animated Tech Stack Section */}
      <section className="tech-slider-section">
        <div className="container">
          <h2 className="section-title">
            <span className="title-text">Our Complete Technology Stack</span>
            <span className="title-underline"></span>
          </h2>
          <p className="section-description">
            We utilize a comprehensive set of tools and technologies to deliver
            exceptional solutions
          </p>

          <div className="tech-slider-container" ref={containerRef}>
            {/* First row */}
            <div className="tech-slider-wrapper">
              <div
                className="tech-slider-row"
                ref={row1Ref}
                style={{ transform: `translateX(-${offset}px)` }}
              >
                {duplicatedRow1.map((tech, index) => (
                  <div key={`row1-${index}`} className="tech-slide">
                    <div className="tech-logo-container">
                      <img
                        src={tech.logo}
                        alt={tech.name}
                        className="tech-logo"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/logos/default.png";
                        }}
                      />
                    </div>
                    <span className="tech-name">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Second row */}
            <div className="tech-slider-wrapper">
              <div
                className="tech-slider-row reverse-animation"
                ref={row2Ref}
                style={{ transform: `translateX(-${offset}px)` }}
              >
                {duplicatedRow2.map((tech, index) => (
                  <div key={`row2-${index}`} className="tech-slide">
                    <div className="tech-logo-container">
                      <img
                        src={tech.logo}
                        alt={tech.name}
                        className="tech-logo"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/logos/default.png";
                        }}
                      />
                    </div>
                    <span className="tech-name">{tech.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Capabilities Section */}
      <section className="ai-capabilities">
        <div className="container">
          <h2 className="section-title">
            <span className="title-text">AI Capabilities</span>
            <span className="title-underline"></span>
          </h2>

          <div className="capabilities-grid">
            <div className="capability-item">
              <div className="capability-icon">
                <img
                  src="/logos/tech_predictive.png"
                  alt="Predictive Analytics"
                />
              </div>
              <h3>Predictive Analytics</h3>
              <p>
                Leverage historical data to predict future outcomes and trends
              </p>
            </div>

            <div className="capability-item">
              <div className="capability-icon">
                <img
                  src="/logos/tech_nlp.png"
                  alt="Natural Language Processing"
                />
              </div>
              <h3>Natural Language Processing</h3>
              <p>Analyze, understand, and generate human language</p>
            </div>

            <div className="capability-item">
              <div className="capability-icon">
                <img
                  src="/logos/tech_computervision.png"
                  alt="Computer Vision"
                />
              </div>
              <h3>Computer Vision</h3>
              <p>Extract information from images and videos for analysis</p>
            </div>

            <div className="capability-item">
              <div className="capability-icon">
                <img
                  src="/logos/tech_recommadation.png"
                  alt="Recommendation Systems"
                />
              </div>
              <h3>Recommendation Systems</h3>
              <p>
                Personalize user experiences with intelligent recommendations
              </p>
            </div>

            <div className="capability-item">
              <div className="capability-icon">
                <img
                  src="/logos/tech_robotic.png"
                  alt="Robotic Process Automation"
                />
              </div>
              <h3>Robotic Process Automation</h3>
              <p>Automate repetitive tasks with AI-powered bots</p>
            </div>

            <div className="capability-item">
              <div className="capability-icon">
                <img src="/logos/tech_anomaly.png" alt="Anomaly Detection" />
              </div>
              <h3>Anomaly Detection</h3>
              <p>
                Identify unusual patterns that do not conform to expected
                behavior
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="tech-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Implement AI in Your Business?</h2>
            <p>
              Let our experts help you leverage the power of artificial
              intelligence to solve complex problems and drive innovation in
              your organization.
            </p>
            <div className="cta-buttons">
              <a href="/contact-us">
                <button className="cta-button primary">
                  Contact Our AI Experts
                </button>
              </a>
              <a href="/contact-us">
                <button className="cta-button outline">
                  View Case Studies
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ToolTechStack;
